// Libs
import React, { useContext, useEffect, useState } from 'react';
// Context
import { ModalBuilderContext } from 'core/context';
// Hooks
import usePaymentApi from 'core/hooks/usePaymentApi';
import { useToastMessage } from 'hooks';
// Components
import { Button, LogoIcon, Modal, Select, Span, TextField, Wrapper } from 'components';

const { Option } = Select;
const { Input } = TextField;

const EMPTY_FORM = {
	name: '',
	iva: '',
	service_ids: [],
};

const CreateInvoicePayment = () => {
	const { payload: modalPayload, cleanModal } = useContext(ModalBuilderContext);
	const { paycutId, refreshPayments } = modalPayload;

	const api = usePaymentApi();
	const sendToast = useToastMessage();

	const [services, setServices] = useState([]);
	const [isFetching, setIsFetching] = useState(true);
	const [form, _setForm] = useState(EMPTY_FORM);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const setForm = values => _setForm(cur => ({ ...cur, ...values }));

	useEffect(() => {
		api.getPaycutById(paycutId)
			.then(paycuts => {
				const paycut = Array.isArray(paycuts) ? paycuts[0] : null;
				if (!paycut?.department_id) {
					sendToast({ message: 'No se pudo obtener el departamento del corte', type: 'error' });
					setIsFetching(false);
					return;
				}
				const paycutIva = paycut.iva != null ? String(paycut.iva) : '';
				setForm({ iva: paycutIva });
				return api.getServicesByDepartment(paycut.department_id);
			})
			.then(data => {
				if (data) setServices(Array.isArray(data) ? data : []);
				setIsFetching(false);
			})
			.catch(err => {
				sendToast({ message: err.message || 'Error al cargar servicios', type: 'error' });
				setIsFetching(false);
			});
	}, []);

	const handleSubmit = () => {
		if (!form.name.trim()) {
			sendToast({ message: 'El nombre es requerido', type: 'warning' });
			return;
		}
		if (!form.service_ids.length) {
			sendToast({ message: 'Selecciona al menos un servicio', type: 'warning' });
			return;
		}

		const iva = form.iva !== '' ? Number(form.iva) : 0.0;

		setIsSubmitting(true);
		api.createInvoicePayment({
			paycut_id: paycutId,
			name: form.name,
			iva: isNaN(iva) ? null : iva,
			service_ids: form.service_ids,
		})
			.then(() => {
				sendToast({ message: 'Cobro creado exitosamente', type: 'success' });
				if (refreshPayments) refreshPayments();
				cleanModal();
			})
			.catch(err => {
				sendToast({ message: err.message || 'Error al crear el cobro', type: 'error' });
				setIsSubmitting(false);
			});
	};

	return (
		<Modal
			title={null}
			closable={false}
			centered={false}
			visible={true}
			onCancel={cleanModal}
			footer={null}
		>
			<Wrapper
				highBackground
				width='100%'
				padding='10px'
				margin='0'
				flexDirection='column'
				alignItems='center'
				justifyContent='flex-start'
			>
				<Wrapper width='95%' height='28px' justifyContent='flex-start'>
					<Span fontSize='xl'>Crear Cobro</Span>
				</Wrapper>

				{isFetching ? (
					<LogoIcon spin />
				) : null}

				<Wrapper
					padding='10px 0'
					width='95%'
					flexDirection='column'
					alignItems='stretch'
					style={{ display: isFetching ? 'none' : 'flex' }}
				>
					<Input
						placeholder='Nombre*'
						maxLength={100}
						value={form.name}
						onChange={e => setForm({ name: e.target.value })}
						style={{ marginBottom: 12 }}
					/>

					<Input
						placeholder='IVA'
						value={form.iva}
						onChange={e => {
							const val = e.target.value;
							if (val === '' || (/^\d{0,3}(\.\d{0,2})?$/.test(val) && Number(val) <= 999.99))
								setForm({ iva: val });
						}}
						style={{ marginBottom: 12 }}
					/>

					<Select
						mode='multiple'
						showSearch
						placeholder='Seleccione los servicios*'
						value={form.service_ids}
						onChange={value => setForm({ service_ids: value })}
						style={{ marginBottom: 12 }}
						filterOption={(input, option) =>
							option?.children?.props?.children
								?.toLowerCase()
								?.includes(input.toLowerCase())
						}
					>
						{services.map(service => (
							<Option key={service.id} value={service.id}>
								<em>{service.name}</em>
							</Option>
						))}
					</Select>

					<Wrapper justifyContent='flex-end' padding='0'>
						{isSubmitting ? (
							<LogoIcon button spin />
						) : (
							<Button
								mode='primary'
								disabled={!form.name.trim() || !form.service_ids.length}
								onClick={handleSubmit}
							>
								CREAR
							</Button>
						)}
					</Wrapper>
				</Wrapper>
			</Wrapper>
		</Modal>
	);
};

export default CreateInvoicePayment;
